import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Label } from '../components/ui/label';
import { 
  FileSpreadsheet, 
  FileText, 
  Calendar,
  Bike,
  Users,
  Battery,
  Wrench,
  Receipt,
  Building2,
  Shield,
  ChevronDown,
  ChevronRight,
  Download,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import useDeviceDetection from '../hooks/useDeviceDetection';
import {
  exportBikeLog,
  exportMaintenanceLog,
  exportBikesAssignedToLessee,
  exportBikesWithRiders,
  exportMonthlyMaintenanceSummary,
  exportFinesReport,
  exportSalikReport,
  exportLesseesReport,
  exportRidersReport,
  exportBatteriesReport,
  exportActivityLogs,
  exportMotorcycleHistory,
  exportRepairItemReport,
  exportBatterySwapsReport,
  exportBatteryHistoryReport
} from '../utils/exportUtils';

const Reports = () => {
  const { t } = useTranslation();
  const [motorcycles, setMotorcycles] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [riders, setRiders] = useState([]);
  const [fines, setFines] = useState([]);
  const [salikRecords, setSalikRecords] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [batteries, setBatteries] = useState([]);
  const [handovers, setHandovers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({});
  const [expandedReports, setExpandedReports] = useState({});
  
  // Report-specific states
  const [reportFilters, setReportFilters] = useState({});
  const [motorcycleHistory, setMotorcycleHistory] = useState(null);
  const [repairItems, setRepairItems] = useState([]);
  const [repairItemReportData, setRepairItemReportData] = useState(null);
  const [batterySwapsReportData, setBatterySwapsReportData] = useState(null);
  const [batteryHistoryData, setBatteryHistoryData] = useState(null);

  useEffect(() => {
    fetchAllData();
    fetchRepairItems();
  }, []);

  const fetchAllData = async () => {
    try {
      const [bikesRes, maintenanceRes, ridersRes, finesRes, salikRes, lesseesRes, batteriesRes, handoversRes] = await Promise.all([
        axios.get(`${API}/motorcycles/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/maintenance?limit=500`, { headers: getAuthHeader() }),
        axios.get(`${API}/riders/dropdown`, { headers: getAuthHeader() }),
        axios.get(`${API}/fines?limit=10000`, { headers: getAuthHeader() }),
        axios.get(`${API}/salik`, { headers: getAuthHeader() }),
        axios.get(`${API}/lessees`, { headers: getAuthHeader() }),
        axios.get(`${API}/batteries`, { headers: getAuthHeader() }),
        axios.get(`${API}/motorcycles/handovers`, { headers: getAuthHeader() })
      ]);

      setMotorcycles(bikesRes.data);
      setMaintenanceRecords(maintenanceRes.data);
      setRiders(ridersRes.data);
      setFines(finesRes.data);
      setSalikRecords(salikRes.data);
      setLessees(lesseesRes.data);
      setBatteries(batteriesRes.data);
      setHandovers(handoversRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRepairItems = async () => {
    try {
      const response = await axios.get(`${API}/maintenance/repair-items`, { headers: getAuthHeader() });
      setRepairItems(response.data);
    } catch (error) {
      console.error('Failed to fetch repair items:', error);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleReport = (reportId) => {
    setExpandedReports(prev => ({
      ...prev,
      [reportId]: !prev[reportId]
    }));
  };

  const updateReportFilter = (reportId, field, value) => {
    setReportFilters(prev => ({
      ...prev,
      [reportId]: {
        ...prev[reportId],
        [field]: value
      }
    }));
  };

  const getReportFilter = (reportId, field) => {
    return reportFilters[reportId]?.[field] || '';
  };

  const filterByDateRange = (data, dateField, dateFrom, dateTo) => {
    if (!dateFrom && !dateTo) return data;
    
    // Parse a date string in various formats to YYYY-MM-DD for comparison
    const normalizeDate = (dateStr) => {
      if (!dateStr || dateStr === 'N/A' || dateStr === 'None' || dateStr === '-') return '';
      const s = String(dateStr).trim();
      if (!s) return '';
      // Already YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
      // DD-MM-YYYY or DD/MM/YYYY
      const parts = s.split(/[-/]/);
      if (parts.length >= 3 && parts[0].length <= 2 && parts[2].length === 4) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
      // YYYY/MM/DD
      if (parts.length >= 3 && parts[0].length === 4) {
        return `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
      }
      return '';
    };
    
    // Date fields to try for each record type
    const dateFields = [
      dateField,
      'fine_date_sortable', 'salik_date_sortable', 'maintenance_date_sortable',
      'date_of_fine', 'fine_date', 'salik_date', 'maintenance_date', 'date'
    ];
    
    return data.filter(item => {
      // Try each date field until we find a valid normalized date
      let itemDate = '';
      for (const field of dateFields) {
        const normalized = normalizeDate(item[field]);
        if (normalized && /^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
          itemDate = normalized;
          break;
        }
      }
      
      if (!itemDate) return false; // No valid date found — exclude
      
      if (dateFrom && itemDate < dateFrom) return false;
      if (dateTo && itemDate > dateTo) return false;
      return true;
    });
  };

  const applyOptionalFilters = (data, reportId) => {
    let filtered = data;
    
    const lesseeFilter = getReportFilter(reportId, 'lessee');
    const riderFilter = getReportFilter(reportId, 'rider');
    const bikeFilter = getReportFilter(reportId, 'bike');
    const locationFilter = getReportFilter(reportId, 'location');
    
    if (lesseeFilter) {
      // Normalize for comparison: remove dots, dashes, extra spaces, common suffixes
      const normalize = (s) => s.toLowerCase().replace(/\./g, '').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
      const lf = normalize(lesseeFilter);
      
      filtered = filtered.filter(item => {
        const fields = [item.lessee_name, item.lessee_company_name, item.lessee].filter(Boolean);
        return fields.some(val => {
          const v = normalize(val);
          return v.includes(lf) || lf.includes(v);
        });
      });
    }
    
    if (riderFilter) {
      filtered = filtered.filter(item => 
        item.rider_name && item.rider_name.toLowerCase().includes(riderFilter.toLowerCase())
      );
    }
    
    if (bikeFilter) {
      filtered = filtered.filter(item => 
        (item.motorcycle_plate && item.motorcycle_plate.toLowerCase().includes(bikeFilter.toLowerCase())) ||
        (item.bike_plate_number && item.bike_plate_number.toLowerCase().includes(bikeFilter.toLowerCase())) ||
        (item.plate_number && item.plate_number.toLowerCase().includes(bikeFilter.toLowerCase()))
      );
    }
    
    if (locationFilter) {
      filtered = filtered.filter(item => 
        item.location_name && item.location_name.toLowerCase().includes(locationFilter.toLowerCase())
      );
    }
    
    return filtered;
  };

  const fetchActivityLogs = async (reportId) => {
    try {
      const dateFrom = getReportFilter(reportId, 'dateFrom');
      const dateTo = getReportFilter(reportId, 'dateTo');
      
      const params = new URLSearchParams();
      if (dateFrom) params.append('start_date', dateFrom);
      if (dateTo) params.append('end_date', dateTo);
      
      const response = await axios.get(`${API}/activity-logs?${params.toString()}`, { 
        headers: getAuthHeader() 
      });
      setActivityLogs(response.data);
      toast.success(`Loaded ${response.data.length} activity records`);
    } catch (error) {
      toast.error('Failed to load activity logs');
    }
  };

  const fetchMotorcycleHistory = async (reportId) => {
    const motorcycleId = getReportFilter(reportId, 'motorcycleId');
    const dateFrom = getReportFilter(reportId, 'dateFrom');
    const dateTo = getReportFilter(reportId, 'dateTo');

    if (!motorcycleId || !dateFrom || !dateTo) {
      toast.error('Please select a motorcycle and date range');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/motorcycles/${motorcycleId}/history?start_date=${dateFrom}&end_date=${dateTo}`,
        { headers: getAuthHeader() }
      );
      setMotorcycleHistory(response.data);
      toast.success('Motorcycle history loaded successfully');
    } catch (error) {
      toast.error('Failed to load motorcycle history');
    }
  };

  const fetchRepairItemReport = async (reportId) => {
    const repairItem = getReportFilter(reportId, 'repairItem');
    const dateFrom = getReportFilter(reportId, 'dateFrom');
    const dateTo = getReportFilter(reportId, 'dateTo');

    if (!repairItem) {
      toast.error('Please select a repair item');
      return;
    }

    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }

    try {
      const params = new URLSearchParams({
        repair_item: repairItem,
        start_date: dateFrom,
        end_date: dateTo
      });
      
      const response = await axios.get(
        `${API}/maintenance/repair-item-report?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setRepairItemReportData(response.data);
      toast.success(`Loaded ${response.data.summary.total_occurrences} records for ${repairItem}`);
    } catch (error) {
      toast.error('Failed to load repair item report');
    }
  };

  const fetchBatterySwapsReport = async (reportId) => {
    const dateFrom = getReportFilter(reportId, 'dateFrom');
    const dateTo = getReportFilter(reportId, 'dateTo');

    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }

    try {
      const params = new URLSearchParams({
        start_date: dateFrom,
        end_date: dateTo
      });
      
      const response = await axios.get(
        `${API}/battery-swaps/report?${params.toString()}`,
        { headers: getAuthHeader() }
      );
      setBatterySwapsReportData(response.data);
      toast.success(`Loaded ${response.data.summary.total_swaps} battery swaps`);
    } catch (error) {
      toast.error('Failed to load battery swaps report');
    }
  };

  const fetchBatteryHistory = async (reportId) => {
    const batterySerial = getReportFilter(reportId, 'batterySerial');

    if (!batterySerial) {
      toast.error('Please select a battery');
      return;
    }

    try {
      const response = await axios.get(
        `${API}/battery-history/${encodeURIComponent(batterySerial)}`,
        { headers: getAuthHeader() }
      );
      setBatteryHistoryData(response.data);
      if (response.data.length === 0) {
        toast.info(`No swap history found for battery ${batterySerial}`);
      } else {
        toast.success(`Loaded ${response.data.length} swap records for ${batterySerial}`);
      }
    } catch (error) {
      toast.error('Failed to load battery history');
      console.error(error);
    }
  };

  const handleExport = async (reportId, format) => {
    try {
      let filteredData;
      const dateFrom = getReportFilter(reportId, 'dateFrom');
      const dateTo = getReportFilter(reportId, 'dateTo');
      const selectedMonth = parseInt(getReportFilter(reportId, 'month')) || new Date().getMonth() + 1;
      const selectedYear = parseInt(getReportFilter(reportId, 'year')) || new Date().getFullYear();
      
      switch (reportId) {
        case 'motorcycle-history':
          if (!motorcycleHistory) {
            toast.error('Please load motorcycle history first');
            return;
          }
          await exportMotorcycleHistory(motorcycleHistory, format);
          break;
        case 'bike-log':
          await exportBikeLog(motorcycles, format);
          break;
        case 'maintenance-log':
          if (!dateFrom || !dateTo) {
            toast.error('Please select date range');
            return;
          }
          filteredData = filterByDateRange(maintenanceRecords, 'maintenance_date', dateFrom, dateTo);
          filteredData = applyOptionalFilters(filteredData, reportId);
          if (filteredData.length === 0) {
            toast.warning('No maintenance records found');
            return;
          }
          await exportMaintenanceLog(filteredData, format);
          break;
        case 'bikes-lessee':
          await exportBikesAssignedToLessee(motorcycles, format);
          break;
        case 'bikes-riders':
          await exportBikesWithRiders(motorcycles, riders, handovers, format);
          break;
        case 'monthly-summary':
          filteredData = applyOptionalFilters(maintenanceRecords, reportId);
          await exportMonthlyMaintenanceSummary(motorcycles, filteredData, selectedMonth, selectedYear, format);
          break;
        case 'fines':
          if (!dateFrom || !dateTo) {
            toast.error('Please select date range');
            return;
          }
          filteredData = filterByDateRange(fines, 'fine_date_sortable', dateFrom, dateTo);
          const inDateRange = filteredData.length;
          filteredData = applyOptionalFilters(filteredData, reportId);
          if (filteredData.length === 0) {
            if (fines.length === 0) {
              toast.warning('No fines loaded — try refreshing the page');
            } else if (inDateRange === 0) {
              toast.warning(`No fines in date range ${dateFrom} to ${dateTo} (${fines.length} fines total in DB)`);
            } else {
              toast.warning(`${inDateRange} fine(s) in date range, but 0 matched the lessee/rider/bike filter. Try clearing the optional filters.`);
            }
            return;
          }
          await exportFinesReport(filteredData, format);
          break;
        case 'salik':
          if (!dateFrom || !dateTo) {
            toast.error('Please select date range');
            return;
          }
          filteredData = filterByDateRange(salikRecords, 'salik_date_sortable', dateFrom, dateTo);
          filteredData = applyOptionalFilters(filteredData, reportId);
          if (filteredData.length === 0) {
            toast.warning('No salik records found');
            return;
          }
          await exportSalikReport(filteredData, format);
          break;
        case 'lessees':
          await exportLesseesReport(lessees, format);
          break;
        case 'riders':
          filteredData = applyOptionalFilters(riders, reportId);
          await exportRidersReport(filteredData, format);
          break;
        case 'batteries':
          await exportBatteriesReport(batteries, format);
          break;
        case 'activity-logs':
          if (activityLogs.length === 0) {
            toast.error('Please load activity logs first');
            return;
          }
          await exportActivityLogs(activityLogs, format);
          break;
        case 'repair-item-report':
          if (!repairItemReportData) {
            toast.error('Please load repair item report first');
            return;
          }
          // Apply optional filters
          const filteredRepairs = applyOptionalFilters(repairItemReportData.records, reportId);
          const filteredRepairData = {
            ...repairItemReportData,
            records: filteredRepairs,
            summary: {
              ...repairItemReportData.summary,
              total_occurrences: filteredRepairs.length
            }
          };
          await exportRepairItemReport(filteredRepairData, format);
          break;
        case 'battery-swaps-report':
          if (!batterySwapsReportData) {
            toast.error('Please load battery swaps report first');
            return;
          }
          // Apply optional filters
          const filteredSwaps = applyOptionalFilters(batterySwapsReportData.swaps, reportId);
          const filteredReportData = {
            ...batterySwapsReportData,
            swaps: filteredSwaps,
            summary: {
              ...batterySwapsReportData.summary,
              total_swaps: filteredSwaps.length
            }
          };
          await exportBatterySwapsReport(filteredReportData, format);
          break;
        case 'battery-history':
          if (!batteryHistoryData) {
            toast.error('Please load battery history first');
            return;
          }
          const selectedBattery = getReportFilter(reportId, 'batterySerial');
          if (!selectedBattery) {
            toast.error('Please select a battery');
            return;
          }
          await exportBatteryHistoryReport(selectedBattery, batteryHistoryData, format);
          break;
        default:
          break;
      }
      toast.success(`Report exported as ${format.toUpperCase()} successfully!`);
    } catch (error) {
      toast.error('Failed to export report');
      console.error(error);
    }
  };

  const months = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' },
    { value: 3, label: 'March' }, { value: 4, label: 'April' },
    { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' },
    { value: 9, label: 'September' }, { value: 10, label: 'October' },
    { value: 11, label: 'November' }, { value: 12, label: 'December' }
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  // Categorized reports with menu bar order
  const reportCategories = [
    {
      id: 'motorcycles',
      title: 'Motorcycles',
      icon: Bike,
      color: 'lime',
      reports: [
        {
          id: 'bike-log',
          title: 'Motorcycle Fleet Report',
          description: 'Complete inventory of all motorcycles with specifications',
          filters: []
        },
        {
          id: 'motorcycle-history',
          title: 'Comprehensive Motorcycle History',
          description: 'Complete lifecycle: riders, maintenance, fines, salik, insurance, accidents',
          filters: ['motorcycleSelect', 'dateRange'],
          needsLoad: true,
          loadAction: fetchMotorcycleHistory,
          loadButtonText: 'Load History'
        },
        {
          id: 'bikes-riders',
          title: 'Motorcycles with Assigned Riders',
          description: 'Fleet report showing rider assignments and handover dates',
          filters: []
        },
        {
          id: 'bikes-lessee',
          title: 'Motorcycles by Lessee Company',
          description: 'Fleet breakdown by lessee companies',
          filters: []
        }
      ]
    },
    {
      id: 'riders',
      title: 'Riders',
      icon: Users,
      color: 'blue',
      reports: [
        {
          id: 'riders',
          title: 'Riders Report',
          description: 'Registered riders with optional lessee filter',
          filters: ['optionalFilters'],
          optionalFilters: ['lessee']
        }
      ]
    },
    {
      id: 'batteries',
      title: 'Batteries',
      icon: Battery,
      color: 'green',
      reports: [
        {
          id: 'batteries',
          title: 'Battery Inventory Report',
          description: 'Battery stock and health status',
          filters: []
        },
        {
          id: 'battery-history',
          title: 'Individual Battery History',
          description: 'Complete swap history for a specific battery',
          filters: ['batterySelect'],
          needsLoad: true,
          loadAction: fetchBatteryHistory,
          loadButtonText: 'Load History'
        },
        {
          id: 'battery-swaps-report',
          title: 'Battery Swaps Report',
          description: 'Swap records with optional location and bike filters',
          filters: ['dateRange', 'optionalFilters'],
          optionalFilters: ['location', 'bike'],
          needsLoad: true,
          loadAction: fetchBatterySwapsReport,
          loadButtonText: 'Load Report'
        }
      ]
    },
    {
      id: 'maintenance',
      title: 'Maintenance',
      icon: Wrench,
      color: 'orange',
      reports: [
        {
          id: 'maintenance-log',
          title: 'Maintenance Records',
          description: 'Maintenance history with optional location and bike filters',
          filters: ['dateRange', 'optionalFilters'],
          optionalFilters: ['location', 'bike']
        },
        {
          id: 'monthly-summary',
          title: 'Monthly Maintenance Summary',
          description: 'Monthly costs with optional lessee, location and bike filters',
          filters: ['monthYear', 'optionalFilters'],
          optionalFilters: ['lessee', 'location', 'bike']
        },
        {
          id: 'repair-item-report',
          title: 'Repair Item Analysis Report',
          description: 'Repair analysis with optional location and bike filters',
          filters: ['repairItemSelect', 'dateRange', 'optionalFilters'],
          optionalFilters: ['location', 'bike'],
          needsLoad: true,
          loadAction: fetchRepairItemReport,
          loadButtonText: 'Load Report'
        }
      ]
    },
    {
      id: 'fines',
      title: 'Fines & Salik',
      icon: Receipt,
      color: 'red',
      reports: [
        {
          id: 'fines',
          title: 'Fines Report',
          description: 'Traffic fines records with optional lessee, rider, bike filters',
          filters: ['dateRange', 'optionalFilters'],
          optionalFilters: ['lessee', 'rider', 'bike']
        },
        {
          id: 'salik',
          title: 'Salik Report',
          description: 'Salik charges with optional lessee, rider, bike filters',
          filters: ['dateRange', 'optionalFilters'],
          optionalFilters: ['lessee', 'rider', 'bike']
        }
      ]
    },
    {
      id: 'lessees',
      title: 'Lessees',
      icon: Building2,
      color: 'purple',
      reports: [
        {
          id: 'lessees',
          title: 'Lessee Companies Report',
          description: 'All registered lessee companies with contact details',
          filters: []
        }
      ]
    },
    {
      id: 'insurance',
      title: 'Insurance',
      icon: Shield,
      color: 'indigo',
      reports: []
    },
    {
      id: 'activity',
      title: 'Activity Logs',
      icon: FileText,
      color: 'slate',
      reports: [
        {
          id: 'activity-logs',
          title: 'User Activity & Transaction Summary',
          description: 'Comprehensive audit log of all system activities (Admin only)',
          filters: ['dateRange'],
          needsLoad: true,
          loadAction: fetchActivityLogs,
          loadButtonText: 'Load Logs'
        }
      ]
    }
  ];

  const renderFilters = (report) => {
    const reportId = report.id;
    const isExpanded = expandedReports[reportId];
    
    if (!isExpanded || !report.filters || report.filters.length === 0) return null;

    return (
      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3" onClick={(e) => e.stopPropagation()}>
        {report.filters.includes('motorcycleSelect') && (
          <div onClick={(e) => e.stopPropagation()}>
            <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Select Motorcycle *
            </Label>
            <SearchableSelect
              options={motorcycles.map(m => ({ 
                value: m.id, 
                label: `${m.plate_number} - ${m.model_name}` 
              }))}
              value={getReportFilter(reportId, 'motorcycleId')}
              onValueChange={(value) => {
                updateReportFilter(reportId, 'motorcycleId', value);
                setMotorcycleHistory(null);
              }}
              placeholder="Search by plate number..."
              emptyMessage="No motorcycles found."
            />
          </div>
        )}
        
        {report.filters.includes('batterySelect') && (
          <div onClick={(e) => e.stopPropagation()}>
            <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Select Battery *
            </Label>
            <SearchableSelect
              options={batteries.map(b => ({ 
                value: b.serial_number, 
                label: `${b.serial_number}${b.status ? ` - ${b.status}` : ''}`
              }))}
              value={getReportFilter(reportId, 'batterySerial')}
              onValueChange={(value) => {
                updateReportFilter(reportId, 'batterySerial', value);
                setBatteryHistoryData(null);
              }}
              placeholder="Search by serial number..."
              emptyMessage="No batteries found."
            />
          </div>
        )}
        
        {report.filters.includes('repairItemSelect') && (
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Select Repair Item *
            </Label>
            <Select
              value={getReportFilter(reportId, 'repairItem')}
              onValueChange={(value) => {
                updateReportFilter(reportId, 'repairItem', value);
                setRepairItemReportData(null);
              }}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="Choose repair item..." />
              </SelectTrigger>
              <SelectContent>
                {repairItems.length === 0 ? (
                  <SelectItem value="no-items" disabled>No repair items found</SelectItem>
                ) : (
                  repairItems.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {report.filters.includes('dateRange') && (
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Date Range *
            </Label>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <Input
                  type="date"
                  value={getReportFilter(reportId, 'dateFrom')}
                  onChange={(e) => {
                    updateReportFilter(reportId, 'dateFrom', e.target.value);
                    if (report.needsLoad) setActivityLogs([]);
                  }}
                  onClick={(e) => { e.stopPropagation(); e.target.showPicker && e.target.showPicker(); }}
                  className="h-9 text-sm w-full cursor-pointer"
                  placeholder="YYYY-MM-DD"
                />
              </div>
              <span className="text-slate-500 text-sm">to</span>
              <div className="flex-1">
                <Input
                  type="date"
                  value={getReportFilter(reportId, 'dateTo')}
                  onChange={(e) => {
                    updateReportFilter(reportId, 'dateTo', e.target.value);
                    if (report.needsLoad) setActivityLogs([]);
                  }}
                  onClick={(e) => { e.stopPropagation(); e.target.showPicker && e.target.showPicker(); }}
                  className="h-9 text-sm w-full cursor-pointer"
                  placeholder="YYYY-MM-DD"
                />
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Click to open calendar picker</p>
          </div>
        )}
        
        {report.filters.includes('monthYear') && (
          <div className="grid grid-cols-2 gap-3" onClick={(e) => e.stopPropagation()}>
            <div>
              <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Month *
              </Label>
              <Select 
                value={getReportFilter(reportId, 'month') || String(new Date().getMonth() + 1)}
                onValueChange={(v) => updateReportFilter(reportId, 'month', v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={String(month.value)}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
                Year *
              </Label>
              <Select 
                value={getReportFilter(reportId, 'year') || String(new Date().getFullYear())}
                onValueChange={(v) => updateReportFilter(reportId, 'year', v)}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year} value={String(year)}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
        
        {/* Optional Filters */}
        {report.filters?.includes('optionalFilters') && report.optionalFilters && (
          <div onClick={(e) => e.stopPropagation()}>
            <Label className="text-xs font-medium text-slate-700 mb-1.5 block">
              Optional Filters (Leave blank for all)
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {report.optionalFilters.includes('lessee') && (
                <SearchableSelect
                  options={lessees.map(lessee => ({
                    value: lessee.company_name,
                    label: lessee.company_name,
                    searchText: `${lessee.contact_email || ''} ${lessee.trade_license_no || ''}`,
                    metadata: {
                      'TRN': lessee.trn_no || lessee.trn_number,
                      'Trade License': lessee.trade_license_no || lessee.trade_license_number
                    }
                  }))}
                  value={getReportFilter(reportId, 'lessee')}
                  onValueChange={(value) => updateReportFilter(reportId, 'lessee', value)}
                  placeholder="Search lessee..."
                  emptyMessage="No lessees found."
                  className="h-9 text-sm"
                  onClick={(e) => e.stopPropagation()}
                  showMetadata={false}
                />
              )}
              {report.optionalFilters.includes('rider') && (
                <SearchableSelect
                  options={riders.map(rider => ({
                    value: rider.name,
                    label: rider.name,
                    searchText: `${rider.emirates_id || ''} ${rider.phone || ''}`,
                    metadata: {
                      'Emirates ID': rider.emirates_id,
                      'Phone': rider.phone
                    }
                  }))}
                  value={getReportFilter(reportId, 'rider')}
                  onValueChange={(value) => updateReportFilter(reportId, 'rider', value)}
                  placeholder="Search rider..."
                  emptyMessage="No riders found."
                  className="h-9 text-sm"
                  onClick={(e) => e.stopPropagation()}
                  showMetadata={false}
                />
              )}
              {report.optionalFilters.includes('bike') && (
                <SearchableSelect
                  options={motorcycles.map(bike => ({
                    value: bike.plate_number,
                    label: bike.plate_number,
                    searchText: `${bike.model_name || ''} ${bike.lessee_name || ''}`,
                    metadata: {
                      'Model': bike.model_name,
                      'Lessee': bike.lessee_name
                    }
                  }))}
                  value={getReportFilter(reportId, 'bike')}
                  onValueChange={(value) => updateReportFilter(reportId, 'bike', value)}
                  placeholder="Search bike plate..."
                  emptyMessage="No bikes found."
                  className="h-9 text-sm"
                  onClick={(e) => e.stopPropagation()}
                  showMetadata={false}
                />
              )}
              {report.optionalFilters.includes('location') && (
                <SearchableSelect
                  options={Array.from(new Set([
                    ...maintenanceRecords.map(m => m.location).filter(Boolean),
                    ...fines.map(f => f.location).filter(Boolean),
                    ...salikRecords.map(s => s.salik_gate).filter(Boolean)
                  ])).map(location => ({
                    value: location,
                    label: location,
                    searchText: location
                  }))}
                  value={getReportFilter(reportId, 'location')}
                  onValueChange={(value) => updateReportFilter(reportId, 'location', value)}
                  placeholder="Search location..."
                  emptyMessage="No locations found."
                  className="h-9 text-sm"
                  onClick={(e) => e.stopPropagation()}
                  showMetadata={false}
                />
              )}
            </div>
          </div>
        )}
        
        {/* Validation Status */}
        <div className="flex items-start gap-2 text-xs">
          {report.filters.includes('repairItemSelect') && !getReportFilter(reportId, 'repairItem') ? (
            <div className="flex items-center gap-1.5 text-red-600">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Please select a repair item</span>
            </div>
          ) : report.filters.includes('dateRange') && 
           (!getReportFilter(reportId, 'dateFrom') || !getReportFilter(reportId, 'dateTo')) ? (
            <div className="flex items-center gap-1.5 text-red-600">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Please select date range</span>
            </div>
          ) : report.filters.includes('motorcycleSelect') && !getReportFilter(reportId, 'motorcycleId') ? (
            <div className="flex items-center gap-1.5 text-red-600">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Please select a motorcycle</span>
            </div>
          ) : report.filters.includes('batterySelect') && !getReportFilter(reportId, 'batterySerial') ? (
            <div className="flex items-center gap-1.5 text-red-600">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>Please select a battery</span>
            </div>
          ) : report.filters.length > 0 ? (
            <div className="flex items-center gap-1.5 text-green-600">
              <CheckCircle className="w-3.5 h-3.5" />
              <span>Ready to generate</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const canExport = (report) => {
    const reportId = report.id;
    
    // Check if report needs to be loaded first
    if (report.needsLoad) {
      if (reportId === 'motorcycle-history') {
        return motorcycleHistory !== null;
      }
      if (reportId === 'activity-logs') {
        return activityLogs.length > 0;
      }
      if (reportId === 'repair-item-report') {
        return repairItemReportData !== null;
      }
      if (reportId === 'battery-swaps-report') {
        return batterySwapsReportData !== null;
      }
      if (reportId === 'battery-history') {
        return batteryHistoryData !== null;
      }
    }
    
    // Check repair item selection
    if (report.filters?.includes('repairItemSelect')) {
      if (!getReportFilter(reportId, 'repairItem')) {
        return false;
      }
    }
    
    // Check date range requirement
    if (report.filters?.includes('dateRange')) {
      if (!getReportFilter(reportId, 'dateFrom') || !getReportFilter(reportId, 'dateTo')) {
        return false;
      }
    }
    
    // Check motorcycle selection
    if (report.filters?.includes('motorcycleSelect')) {
      if (!getReportFilter(reportId, 'motorcycleId')) {
        return false;
      }
    }
    
    // Check battery selection
    if (report.filters?.includes('batterySelect')) {
      if (!getReportFilter(reportId, 'batterySerial')) {
        return false;
      }
    }
    
    return true;
  };

  const ReportItem = ({ report }) => {
    const isExpanded = expandedReports[report.id];
    const exportReady = canExport(report);
    
    return (
      <div className="border-b last:border-b-0">
        <div
          className="px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
          onClick={() => toggleReport(report.id)}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                )}
                <h4 className="text-sm font-medium text-slate-900">{report.title}</h4>
              </div>
              <p className="text-xs text-slate-500 mt-0.5 ml-6">{report.description}</p>
            </div>
            
            {isExpanded && (
              <div className="flex items-center gap-1.5 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {report.needsLoad && !exportReady ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => report.loadAction(report.id)}
                    disabled={
                      (report.filters?.includes('dateRange') && 
                       (!getReportFilter(report.id, 'dateFrom') || !getReportFilter(report.id, 'dateTo'))) ||
                      (report.filters?.includes('batterySelect') && 
                       !getReportFilter(report.id, 'batterySerial')) ||
                      (report.filters?.includes('motorcycleSelect') && 
                       !getReportFilter(report.id, 'motorcycleId')) ||
                      (report.filters?.includes('repairItemSelect') && 
                       !getReportFilter(report.id, 'repairItem'))
                    }
                    className="text-xs px-3 h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {report.loadButtonText}
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleExport(report.id, 'excel')}
                      disabled={!exportReady}
                      className="h-8 px-3 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 disabled:opacity-50"
                      title="Export as Excel"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-1" />
                      Excel
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleExport(report.id, 'pdf')}
                      disabled={!exportReady}
                      className="h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                      title="Export as PDF"
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        
        {isExpanded && renderFilters(report)}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-800">{t('reports.title')}</h1>
        <p className="text-slate-600 mt-1">Select a report to configure and export in PDF or Excel format</p>
      </div>

      {/* Categorized Reports */}
      <div className="space-y-3">
        {reportCategories.map((category) => {
          if (category.reports.length === 0) return null;
          
          const Icon = category.icon;
          const isExpanded = expandedSections[category.id];
          
          return (
            <Card key={category.id} className="overflow-hidden">
              <button
                onClick={() => toggleSection(category.id)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white hover:from-slate-100 hover:to-slate-50 transition-all border-b"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-${category.color}-100`}>
                    <Icon className={`w-5 h-5 text-${category.color}-600`} />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-semibold text-slate-900">{category.title}</h3>
                    <p className="text-xs text-slate-500">
                      {category.reports.length} report{category.reports.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                )}
              </button>
              
              {isExpanded && (
                <div className="bg-white">
                  {category.reports.map((report) => (
                    <ReportItem key={report.id} report={report} />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Reports;
